import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import PersonaBadge from '../common/PersonaBadge';
import { FiChevronLeft, FiChevronRight, FiClock, FiMessageSquare, FiLayers, FiPackage } from 'react-icons/fi';

interface SessionSidebarProps {
  defaultCollapsed?: boolean;
  session: {
    id: string;
    persona_id?: string;
    persona_name?: string;
    phase?: string;
    phase_count?: number;
    message_count?: number;
    created_at?: string;
    updated_at?: string;
    artifacts?: { id: string; name: string; type: string; created_at?: string }[];
    agents?: any[];
    [key: string]: any;
  };
}

function duration(start: string, end?: string): string {
  const s = new Date(start).getTime();
  if (isNaN(s)) return '—';
  const e = end ? new Date(end).getTime() : Date.now();
  const diff = e - s;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  return `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
}

const SessionSidebar: React.FC<SessionSidebarProps> = ({ session, defaultCollapsed = false }) => {
  const { darkMode } = useTheme();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const sectionClass = `rounded-lg shadow-lg border-l-4 p-3 mb-3 ${
    darkMode ? 'bg-gray-800 border-emerald-600' : 'bg-white border-emerald-500 shadow-sm'
  }`;
  const headerClass = 'text-xs font-semibold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-teal-400';
  const labelClass = `flex items-center gap-1.5 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`;
  const valueClass = `text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`;

  if (collapsed) {
    return (
      <div
        className={`w-8 shrink-0 flex flex-col items-center pt-2 cursor-pointer ${
          darkMode ? 'border-l border-gray-700' : 'border-l border-gray-200'
        }`}
        onClick={() => setCollapsed(false)}
      >
        <FiChevronLeft className={`w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
      </div>
    );
  }

  return (
    <div className={`w-72 shrink-0 pl-4 ${darkMode ? 'border-l border-gray-700/50' : 'border-l border-gray-200'}`}>
      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(true)}
        className={`mb-3 p-1 rounded ${darkMode ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-100 text-gray-400'}`}
      >
        <FiChevronRight className="w-4 h-4" />
      </button>

      {/* Persona Card */}
      {session.persona_id && (
        <div className={sectionClass}>
          <h3 className={headerClass}>Persona</h3>
          <div className="flex items-center gap-2 mb-2">
            <PersonaBadge
              personaId={session.persona_id}
              personaName={session.persona_name || 'Unknown'}
              size="md"
              clickable={false}
            />
          </div>
          <div className="flex gap-1">
            <Link
              to={`/personas/forge?personaId=${session.persona_id}`}
              className={`text-[10px] px-1.5 py-0.5 rounded ${
                darkMode ? 'text-emerald-400 hover:bg-emerald-900/20' : 'text-emerald-600 hover:bg-emerald-50'
              }`}
            >
              Open in Forge
            </Link>
          </div>
        </div>
      )}

      {/* Session Insights */}
      <div className={sectionClass}>
        <h3 className={headerClass}>Session Insights</h3>
        <div className="space-y-1.5">
          {session.created_at && (
            <div className="flex items-center justify-between">
              <span className={labelClass}>
                <FiClock className="w-3 h-3" /> Duration
              </span>
              <span className={valueClass}>
                {duration(session.created_at, session.updated_at)}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className={labelClass}>
              <FiMessageSquare className="w-3 h-3" /> Messages
            </span>
            <span className={valueClass}>{session.message_count ?? 0}</span>
          </div>
          {session.phase && (
            <div className="flex items-center justify-between">
              <span className={labelClass}>
                <FiLayers className="w-3 h-3" /> Phase
              </span>
              <span className={`${valueClass} truncate max-w-[120px]`}>{session.phase}</span>
            </div>
          )}
          {session.agents && session.agents.length > 0 && (
            <div className="flex items-center justify-between">
              <span className={labelClass}>
                <FiLayers className="w-3 h-3" /> Agents
              </span>
              <span className={valueClass}>{session.agents.length}</span>
            </div>
          )}
        </div>
      </div>

      {/* Artifacts Produced */}
      {session.artifacts && session.artifacts.length > 0 && (
        <div className={sectionClass}>
          <h3 className={headerClass}>Artifacts ({session.artifacts.length})</h3>
          <div className="space-y-1 max-h-[200px] overflow-y-auto">
            {session.artifacts.map((a: any, i: number) => (
              <div
                key={a.id || i}
                className={`flex items-center gap-2 text-xs py-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
              >
                <FiPackage className="w-3 h-3 shrink-0 text-emerald-400" />
                <span className="truncate flex-1">{a.name || a.type || 'Artifact'}</span>
                <span className={darkMode ? 'text-gray-600' : 'text-gray-400'}>{a.type}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionSidebar;
